'use client';

import { useCallback, useEffect, useState } from 'react';
import { eventWorkflowApi, eventsApi, getApiErrorMessage } from '@/lib/api';

export type TicketRecord = {
  id: string;
  name: string;
  price?: number | string;
  currency?: string;
  quantity_available?: number;
  quantity_sold?: number;
  sale_start?: string | null;
  sale_end?: string | null;
  visibility?: string;
  visibility_mode?: string;
  is_active?: boolean;
  approval_required?: boolean;
  waitlist_enabled?: boolean;
  [key: string]: any;
};

// Global event bus for instant ticket invalidation across components
type TicketListener = (eventId: string) => void;
const listeners = new Set<TicketListener>();

export function notifyTicketsChanged(eventId: string) {
  listeners.forEach((listener) => listener(eventId));
}

export function useEventTickets(eventId?: string | null) {
  const [tickets, setTickets] = useState<TicketRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    if (!eventId || eventId.startsWith('new-') || eventId === '0') {
      setTickets([]);
      setLoading(false);
      setError(null);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      // 1. Primary: fetch draft which includes event.tickets
      const draftRes = await eventWorkflowApi.getDraft(eventId, { fresh: true });
      const rawEvent = draftRes.data?.data?.event;
      if (rawEvent && Array.isArray(rawEvent.tickets)) {
        setTickets(rawEvent.tickets);
        setLoading(false);
        return rawEvent.tickets;
      }

      // 2. Fallback: fetch directly via showEvent
      const showRes = await eventsApi.showEvent(eventId);
      const showData = showRes.data?.data ?? showRes.data;
      const rawTickets = Array.isArray(showData?.tickets) ? showData.tickets : [];
      setTickets(rawTickets);
      setLoading(false);
      return rawTickets;
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to load tickets');
      setError(msg);
      setTickets([]);
      setLoading(false);
      return [];
    }
  }, [eventId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Subscribe to global ticket changes for real-time reactivity
  useEffect(() => {
    if (!eventId) return;
    const handleChanged = (changedEventId: string) => {
      if (changedEventId === eventId) {
        fetchTickets();
      }
    };
    listeners.add(handleChanged);
    return () => {
      listeners.delete(handleChanged);
    };
  }, [eventId, fetchTickets]);

  return {
    tickets,
    setTickets,
    loading,
    error,
    refetch: fetchTickets,
  };
}
